// ExpertiseSection.js - Version avec animations plus raffinées
import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { 
  MdArrowForward, 
  MdCheckCircle, 
  MdWeb,
  MdSmartToy,
  MdTrendingUp,
  MdEdit,
  MdPalette,
  MdVideocam,
  MdCode,
  MdRocketLaunch,
  MdOutlineLightbulb,
  MdOutlineSpeed,
  MdOutlineSecurity,
  MdOutlineSupportAgent,
  MdAutoAwesome
} from 'react-icons/md';
import { FaRobot, FaChartLine, FaPenFancy, FaPaintBrush, FaFilm, FaGlobe } from 'react-icons/fa';
import { GiAstronautHelmet, GiGalaxy, GiStarsStack, GiSpiralShell, GiCrystalBall } from 'react-icons/gi';
import './ExpertiseSection.css';

const ExpertiseSection = () => {
  const [expandedService, setExpandedService] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isCursorActive, setIsCursorActive] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const sectionRef = useRef(null);
  let cursorTimeout;

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
      setIsCursorActive(true);
      
      clearTimeout(cursorTimeout);
      cursorTimeout = setTimeout(() => setIsCursorActive(false), 100);
    };
    
    const handleScroll = () => {
      if (sectionRef.current) {
        const rect = sectionRef.current.getBoundingClientRect();
        const scrollPercent = Math.min(1, Math.max(0, -rect.top / (rect.height - window.innerHeight)));
        setScrollProgress(scrollPercent);
      }
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(cursorTimeout);
    };
  }, []);

  const services = [
    {
      id: 1,
      icon: <MdWeb />,
      iconAlt: <FaGlobe />,
      title: "Développement Web & Mobile",
      problem: "Votre entreprise n'a pas de présence digitale crédible, ou votre site actuel ne convertit pas.",
      solution: "On crée des sites web, applications web et applications mobiles sur mesure — rapides, modernes et pensés pour convertir vos visiteurs en clients.",
      concrete: ["Sites vitrines", "E-commerce", "Applications web métier", "Applications mobiles iOS & Android"],
      technologies: ["React", "Next.js", "React Native", "Node.js", "TailwindCSS"],
      color: "#3B82F6",
      gradient: "linear-gradient(135deg, #3B82F6, #1A4FBD)",
      glowColor: "rgba(59,130,246,0.3)"
    },
    {
      id: 2,
      icon: <MdSmartToy />,
      iconAlt: <FaRobot />,
      title: "Agents IA & Automatisation",
      problem: "Vos équipes perdent du temps sur des tâches répétitives qui pourraient se faire automatiquement.",
      solution: "On déploie des agents IA et des automatisations sur mesure qui prennent en charge les tâches sans valeur — pour que votre équipe se concentre sur l'essentiel.",
      concrete: ["Automatisation de processus", "Agents IA métier", "Intégrations entre outils", "Workflows automatisés"],
      technologies: ["OpenAI", "Make.com", "Zapier", "LangChain", "Python"],
      color: "#3B82F6",
      gradient: "linear-gradient(135deg, #3B82F6, #1A4FBD)",
      glowColor: "rgba(59,130,246,0.3)"
    },
    {
      id: 3,
      icon: <MdTrendingUp />,
      iconAlt: <FaChartLine />,
      title: "Stratégie Commerciale & SEO",
      problem: "Vous n'avez pas assez de clients, votre visibilité en ligne est faible, votre pipeline commercial est vide.",
      solution: "On structure votre stratégie pour attirer plus de clients qualifiés — via le référencement naturel, la prospection et les outils commerciaux adaptés.",
      concrete: ["SEO (référencement naturel)", "Stratégie commerciale", "Mise en place CRM", "Automatisation prospection"],
      technologies: ["Google Analytics", "Semrush", "HubSpot", "Salesforce", "LinkedIn Sales Nav"],
      color: "#3B82F6",
      gradient: "linear-gradient(135deg, #3B82F6, #1A4FBD)",
      glowColor: "rgba(59,130,246,0.3)"
    },
    {
      id: 4,
      icon: <MdEdit />,
      iconAlt: <FaPenFancy />,
      title: "Copywriting & Contenu",
      problem: "Vos textes ne convainquent pas. Votre site parle de vous, pas de votre client.",
      solution: "On écrit des contenus qui parlent directement aux frustrations de vos clients — pour que chaque mot travaille à convertir.",
      concrete: ["Copywriting landing page", "Emails commerciaux", "Fiches produits", "Contenus réseaux sociaux"],
      technologies: ["Storytelling", "SEO Writing", "Email Marketing", "Content Strategy"],
      color: "#3B82F6",
      gradient: "linear-gradient(135deg, #3B82F6, #1A4FBD)",
      glowColor: "rgba(59,130,246,0.3)"
    },
    {
      id: 5,
      icon: <MdPalette />,
      iconAlt: <FaPaintBrush />,
      title: "Design Graphique & Identité",
      problem: "Votre image de marque n'inspire pas confiance, votre logo est vieillissant.",
      solution: "On crée ou refonte votre identité visuelle pour projeter une image professionnelle qui rassure et attire.",
      concrete: ["Logo & charte graphique", "Supports de communication", "Design UI/UX", "Présentations commerciales"],
      technologies: ["Figma", "Adobe Suite", "Canva Pro", "Brand Strategy"],
      color: "#3B82F6",
      gradient: "linear-gradient(135deg, #3B82F6, #1A4FBD)",
      glowColor: "rgba(59,130,246,0.3)"
    },
    {
      id: 6,
      icon: <MdVideocam />,
      iconAlt: <FaFilm />,
      title: "Montage Vidéo & Motion",
      problem: "Vous avez du contenu vidéo brut mais pas les ressources pour le valoriser.",
      solution: "On prend en charge le montage et la production vidéo pour que vos contenus soient percutants.",
      concrete: ["Montage vidéo", "Reels & Shorts", "Vidéos de présentation", "Motion design"],
      technologies: ["Premiere Pro", "After Effects", "DaVinci Resolve", "CapCut"],
      color: "#3B82F6",
      gradient: "linear-gradient(135deg, #3B82F6, #1A4FBD)",
      glowColor: "rgba(59,130,246,0.3)"
    }
  ];

  const toggleExpand = (id) => {
    setExpandedService(expandedService === id ? null : id);
  };

  return (
    <section className="expertise-section-root" ref={sectionRef}>
      <motion.div 
        className={`cursor-glow ${isCursorActive ? 'active' : ''}`}
        animate={{
          x: mousePosition.x - 300,
          y: mousePosition.y - 300,
        }}
        transition={{
          type: "spring",
          damping: 30,
          stiffness: 200,
          mass: 0.5
        }}
      />

      <div className="particles-container">
        {[...Array(40)].map((_, i) => (
          <div
            key={i}
            className="particle"
            style={{
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 20}s`,
              animationDuration: `${25 + Math.random() * 30}s`,
              width: `${2 + Math.random() * 4}px`,
              height: `${2 + Math.random() * 4}px`,
            }}
          />
        ))}
      </div>

      <div className="animated-orbs">
        <div className="orb orb-1" style={{ transform: `translate(${scrollProgress * 120}px, ${scrollProgress * 60}px)` }} />
        <div className="orb orb-2" style={{ transform: `translate(-${scrollProgress * 100}px, -${scrollProgress * 70}px)` }} />
        <div className="orb orb-3" style={{ transform: `translate(${scrollProgress * 80}px, -${scrollProgress * 50}px)` }} />
        <div className="orb orb-4" style={{ transform: `translate(-${scrollProgress * 50}px, ${scrollProgress * 80}px)` }} />
      </div>

      <div className="floating-elements">
        <GiGalaxy className="floating floating-1" />
        <GiSpiralShell className="floating floating-2" />
        <GiAstronautHelmet className="floating floating-3" />
        <GiStarsStack className="floating floating-4" />
        <GiCrystalBall className="floating floating-5" />
      </div>

      <div className="expertise-container">
        <motion.div 
          className="expertise-header"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7, ease: [0.2, 0.9, 0.4, 1] }}
        >
          <motion.div 
            className="expertise-badge-wrapper"
            whileHover={{ scale: 1.03 }}
            transition={{ type: "spring", stiffness: 400, damping: 25 }}
          >
            <span className="expertise-badge">
              <GiAstronautHelmet className="expertise-badge-icon" />
              <span>L'Excellence Digitale</span>
              <GiStarsStack className="expertise-badge-icon" />
            </span>
            <div className="expertise-badge-glow"></div>
          </motion.div>
          
          <motion.h2 
            className="expertise-title"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <span className="title-line">
              <span className="title-gradient">Transformez</span> votre vision
            </span>
            <span className="title-line">
              en <span className="title-highlight">réalité digitale</span>
            </span>
            <div className="title-underline"></div>
          </motion.h2>
          
          <motion.p 
            className="expertise-subtitle"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Une approche globale qui combine technologie, créativité et stratégie 
            <br />pour propulser votre entreprise vers de nouveaux sommets.
          </motion.p>
        </motion.div>

        <div className="expertise-horizontal-list">
          {services.map((service, index) => (
            <HorizontalCard
              key={service.id}
              service={service}
              index={index}
              isExpanded={expandedService === service.id}
              onToggle={() => toggleExpand(service.id)}
            />
          ))}
        </div>

        <motion.div 
          className="expertise-cta-wrapper"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.7, delay: 0.3 }}
        >
          <div className="expertise-cta-card">
            <div className="expertise-cta-glow"></div>
            <div className="cta-pattern"></div>
            
            <div className="expertise-cta-content">
              <motion.div 
                className="expertise-cta-icon"
                animate={{ rotate: 360 }}
                transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
              >
                <MdAutoAwesome />
              </motion.div>
              <h3>Prêt à <span className="gradient-text">transformer</span> votre entreprise ?</h3>
              <p>Rejoignez les entreprises qui nous font confiance et donnez vie à vos projets les plus ambitieux.</p>
              
              <div className="cta-features">
                <div className="cta-feature">
                  <MdOutlineSpeed />
                  <span>Résultats rapides</span>
                </div>
                <div className="cta-feature">
                  <MdOutlineSecurity />
                  <span>100% sécurisé</span>
                </div>
                <div className="cta-feature">
                  <MdOutlineSupportAgent />
                  <span>Support dédié</span>
                </div>
              </div>
            </div>
            
            <motion.button 
              className="expertise-cta-button"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <span>Lancer mon projet</span>
              <MdRocketLaunch />
            </motion.button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Horizontal Card Component
const HorizontalCard = ({ service, index, isExpanded, onToggle }) => {
  const cardRef = useRef(null);
  const isInView = useInView(cardRef, { 
    once: false, 
    amount: 0.3,
    margin: "-50px"
  });

  const [cardScrollProgress, setCardScrollProgress] = useState(0);
  
  useEffect(() => {
    const handleCardScroll = () => {
      if (cardRef.current) {
        const rect = cardRef.current.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        
        const visibleStart = Math.max(0, -rect.top);
        const visibleEnd = Math.min(windowHeight, rect.bottom);
        const visibleHeight = Math.max(0, visibleEnd - visibleStart);
        
        let progress = visibleHeight / rect.height;
        progress = Math.min(1, Math.max(0, progress));
        
        setCardScrollProgress(progress);
      }
    };
    
    window.addEventListener('scroll', handleCardScroll);
    handleCardScroll();
    
    return () => window.removeEventListener('scroll', handleCardScroll);
  }, []);

  const getProgressColor = () => {
    if (cardScrollProgress < 0.33) return "#3B82F6";
    if (cardScrollProgress < 0.66) return "#60A5FA";
    return "#3B82F6";
  };

  const progressColor = getProgressColor();

  return (
    <motion.div
      ref={cardRef}
      className={`horizontal-card ${isExpanded ? 'expanded' : ''}`}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ 
        duration: 0.6, 
        delay: index * 0.1,
        ease: [0.2, 0.9, 0.4, 1]
      }}
    >
      <div 
        className="horizontal-card-glow" 
        style={{ 
          background: `radial-gradient(circle at 30% 50%, ${service.glowColor}, transparent 70%)`,
          opacity: isExpanded ? 0.4 : 0.15
        }}
      />

      <motion.div 
        className="horizontal-card-progress"
        style={{ 
          background: `linear-gradient(90deg, ${progressColor}, ${progressColor}80)`,
        }}
        animate={{ scaleX: cardScrollProgress }}
        transition={{ duration: 0.2 }}
      />
      
      <div className="progress-dots">
        <div className="progress-dot" style={{ background: cardScrollProgress >= 0.33 ? progressColor : "#2A3A5C" }} />
        <div className="progress-dot" style={{ background: cardScrollProgress >= 0.66 ? progressColor : "#2A3A5C" }} />
        <div className="progress-dot" style={{ background: cardScrollProgress >= 0.9 ? progressColor : "#2A3A5C" }} />
      </div>

      <div className="horizontal-card-inner" onClick={onToggle}>
        <div className="horizontal-card-left">
          <motion.div 
            className="horizontal-icon-wrapper"
            whileHover={{ scale: 1.08 }}
            transition={{ duration: 0.3 }}
          >
            <div className="horizontal-icon" style={{ color: service.color }}>
              {service.icon}
            </div>
            <div className="horizontal-icon-pulse" style={{ background: `${service.color}15` }} />
          </motion.div>
          
          <div className="horizontal-title-section">
            <div className="horizontal-card-number" style={{ color: `${service.color}40` }}>
              {String(service.id).padStart(2, '0')}
            </div>
            <h3 className="horizontal-card-title">{service.title}</h3>
            <div className="horizontal-card-tags">
              <span className="tag">{service.concrete.length} services</span>
              <span className="tag">{service.technologies.length} technologies</span>
            </div>
          </div>
        </div>

        <div className="horizontal-card-middle">
          <div className="problem-preview">
            <MdOutlineLightbulb className="preview-icon" style={{ color: service.color }} />
            <p>{service.problem.substring(0, 75)}...</p>
          </div>
        </div>

        <div className="horizontal-card-right">
          <motion.div 
            className="expand-indicator"
            animate={{ rotate: isExpanded ? 180 : 0 }}
            style={{ borderColor: `${service.color}40` }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M6 9L12 15L18 9" stroke={service.color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </motion.div>
          <span className="expand-text">
            {isExpanded ? 'Fermer' : 'Explorer'}
          </span>
        </div>
      </div>

      <AnimatePresence>
        {isExpanded && (
          <motion.div 
            className="horizontal-card-expanded"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.4, ease: [0.2, 0.9, 0.4, 1] }}
          >
            <div className="expanded-content">
              <div className="expanded-solution">
                <div className="section-header" style={{ borderLeftColor: service.color }}>
                  <MdCheckCircle className="section-icon" style={{ color: service.color }} />
                  <h4>Notre Solution</h4>
                </div>
                <p>{service.solution}</p>
              </div>

              <div className="expanded-concrete">
                <div className="section-header" style={{ borderLeftColor: service.color }}>
                  <MdRocketLaunch className="section-icon" style={{ color: service.color }} />
                  <h4>Ce qu'on réalise</h4>
                </div>
                <div className="concrete-grid">
                  {service.concrete.map((item, idx) => (
                    <motion.div 
                      key={idx} 
                      className="concrete-item"
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.05 }}
                      whileHover={{ x: 5 }}
                    >
                      <div className="concrete-dot" style={{ background: service.color }} />
                      <span>{item}</span>
                    </motion.div>
                  ))}
                </div>
              </div>

              <div className="expanded-tech">
                <div className="section-header" style={{ borderLeftColor: service.color }}>
                  <MdCode className="section-icon" style={{ color: service.color }} />
                  <h4>Technologies & Outils</h4>
                </div>
                <div className="tech-cloud">
                  {service.technologies.map((tech, idx) => (
                    <motion.span 
                      key={idx} 
                      className="tech-bubble"
                      style={{ 
                        borderColor: `${service.color}25`,
                        color: service.color
                      }}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: idx * 0.03 }}
                      whileHover={{ y: -2 }}
                    >
                      {tech}
                    </motion.span>
                  ))}
                </div>
              </div>

              <motion.button 
                className="horizontal-cta"
                style={{ background: service.gradient }}
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
                onClick={(e) => e.stopPropagation()}
              >
                <span>En savoir plus</span>
                <MdArrowForward className="cta-icon" />
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default ExpertiseSection;